// The shell's cross-cutting UI STATE — the small amount of client-only view state the shell coordinates
// across the topbar launcher, the chat dock, the modal mounts, and the toasts. NO business state lives here
// (the dashboard owns none, spec §3); this is purely which panel is open and what transient toast shows.
//
// The chat surface is a three-state machine recreated from the prototype (design brief §4): closed →
// `compact` floating panel → `full` page. Escape steps DOWN one level (full → compact → closed). A separate
// `historyOpen` flag flips the compact panel / opens the full-page rail (the history view). The Task modal
// and toast are addressed by an opaque id/message; email is read in place in its owning conversation.

import { useCallback, useRef, useState } from 'react';
import {
  activateChatDraftContext,
  activeChatDraft,
  bindActiveChatDraftToSession,
  createChatDraftRegistry,
  localChatDraftKey,
  sessionChatDraftKey,
  writeActiveChatDraft,
  type ChatDraftContextKey,
  type ChatDraftRegistry,
} from './chat-draft-registry.ts';

/** The chat surface's three states (design brief §4). `closed` renders only the floating launcher. */
export type ChatState = 'closed' | 'compact' | 'full';

/** A pending Memory-browser deep-focus (BUILD-DEC-098): a node to focus + the tree area to open at. Set
 *  by a clickable memory activity row; consumed once by the Memory surface, which then clears it. */
export interface MemoryFocus {
  readonly nodeId: string;
  readonly areaPath: string;
}

/** A surface-requested fresh private chat. The dock consumes it exactly once after invalidating old work. */
export interface FreshChatRequest {
  readonly id: number;
  readonly contextKey: ChatDraftContextKey;
  readonly draft: string;
}

/** The transient shell view-state and the actions that drive it. Consumed via the ShellContext. */
export interface ShellState {
  readonly chat: ChatState;
  readonly historyOpen: boolean;
  /** The open task-detail modal's id (a Task ref), or null. Real content wires in a later stage. */
  readonly taskModalId: string | null;
  /** A pending Memory-browser deep-focus request (BUILD-DEC-098), or null. The App switches to the Memory
   *  surface when set; the Memory surface applies it as its initial focus and then clears it. */
  readonly memoryFocus: MemoryFocus | null;
  /** The current transient toast message, or null. */
  readonly toast: string | null;
  /** The active private-chat context's unsent draft (tab-memory only, bounded by chat-draft-registry). */
  readonly chatDraft: string;
  /** A staged fresh-chat handoff. The dock must invalidate the old controller before applying it. */
  readonly pendingFreshChat: FreshChatRequest | null;

  readonly openChat: () => void;
  /** Open a FRESH private-chat context with a prefill; never reuses the currently open Session. */
  readonly openNewChatWithDraft: (draft: string) => void;
  /** Apply the matching staged handoff after `useChat.newConversation()` has run. */
  readonly applyFreshChat: (requestId: number) => void;
  /** Update only the active context's draft. */
  readonly setChatDraft: (draft: string) => void;
  /** Switch to an unsent blank context after the controller has invalidated the old conversation. */
  readonly beginBlankChatDraft: () => void;
  /** Restore the retained draft for a history Session after the controller starts that switch. */
  readonly openChatSessionDraft: (sessionId: string) => void;
  /** Associate a newly minted/resumed Session with the active draft context. */
  readonly bindChatDraftSession: (sessionId: string) => void;
  readonly closeChat: () => void;
  readonly setChatFull: (full: boolean) => void;
  readonly toggleHistory: () => void;
  readonly closeHistory: () => void;
  /** Escape behaviour: step DOWN one level (task → full → compact → closed). Returns true if it
   *  consumed the key (so the shell knows a level was stepped and can stop). */
  readonly stepDown: () => boolean;
  readonly openTaskModal: (id: string) => void;
  readonly closeTaskModal: () => void;
  /** Request the Memory browser open focused on a node (BUILD-DEC-098) — a clickable memory activity row. */
  readonly openMemoryNode: (nodeId: string, areaPath?: string) => void;
  /** Clear the pending Memory focus once the Memory surface has applied it. */
  readonly clearMemoryFocus: () => void;
  readonly showToast: (message: string) => void;
  readonly clearToast: () => void;
}

/** The raw useState tuples the shell machine drives. Grouped so the main hook stays under the size ceiling. */
interface ShellStores {
  readonly chat: ChatState;
  readonly setChat: (next: ChatState) => void;
  readonly historyOpen: boolean;
  readonly setHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  readonly taskModalId: string | null;
  readonly setTaskModalId: (next: string | null) => void;
  readonly memoryFocus: MemoryFocus | null;
  readonly setMemoryFocus: (next: MemoryFocus | null) => void;
  readonly toast: string | null;
  readonly setToast: (next: string | null) => void;
  readonly chatDraftRegistry: ChatDraftRegistry;
  readonly setChatDraftRegistry: React.Dispatch<React.SetStateAction<ChatDraftRegistry>>;
  readonly pendingFreshChat: FreshChatRequest | null;
  readonly setPendingFreshChat: (next: FreshChatRequest | null) => void;
  readonly localChatSequence: React.MutableRefObject<number>;
  readonly freshChatSequence: React.MutableRefObject<number>;
}

/** All the stable action callbacks (openChat, stepDown, …), derived from the stores. */
type ShellActions = Omit<
  ShellState,
  keyof Pick<
    ShellState,
    | 'chat'
    | 'historyOpen'
    | 'taskModalId'
    | 'memoryFocus'
    | 'toast'
    | 'chatDraft'
    | 'pendingFreshChat'
  >
>;

/** Build the stable action callbacks. `stepDown` reads the current stores for the most-nested-first ladder. */
/** Escape steps down exactly one level, most-nested first (design brief §4): task → chat
 *  full→compact→closed. Reads the live stores off the closure so the ladder always sees current state. */
function useStepDown(stores: ShellStores): () => boolean {
  const { setChat, setHistoryOpen, setTaskModalId } = stores;
  return useCallback((): boolean => {
    if (stores.taskModalId !== null) {
      setTaskModalId(null);
      return true;
    }
    if (stores.chat === 'full') {
      setChat('compact');
      return true;
    }
    if (stores.chat === 'compact') {
      setChat('closed');
      setHistoryOpen(false);
      return true;
    }
    return false;
  }, [stores, setChat, setHistoryOpen, setTaskModalId]);
}

type FreshChatActions = Pick<ShellState, 'openNewChatWithDraft' | 'applyFreshChat'>;

/** Stage/consume the one fresh-chat command; only the dock is allowed to consume it. */
function useFreshChatActions(stores: ShellStores): FreshChatActions {
  const { setChat, setHistoryOpen, setPendingFreshChat, setChatDraftRegistry } = stores;
  const openNewChatWithDraft = useCallback(
    (draft: string) => {
      const contextKey = localChatDraftKey((stores.localChatSequence.current += 1));
      const id = (stores.freshChatSequence.current += 1);
      setPendingFreshChat({ id, contextKey, draft });
      setHistoryOpen(false);
      setChat('compact');
    },
    [
      stores.localChatSequence,
      stores.freshChatSequence,
      setChat,
      setHistoryOpen,
      setPendingFreshChat,
    ],
  );
  const applyFreshChat = useCallback(
    (requestId: number) => {
      const request = stores.pendingFreshChat;
      if (request === null || request.id !== requestId) return;
      setChatDraftRegistry((registry) =>
        activateChatDraftContext(registry, request.contextKey, request.draft),
      );
      setPendingFreshChat(null);
    },
    [stores.pendingFreshChat, setChatDraftRegistry, setPendingFreshChat],
  );
  return { openNewChatWithDraft, applyFreshChat };
}

type DraftContextActions = Pick<
  ShellState,
  'setChatDraft' | 'beginBlankChatDraft' | 'openChatSessionDraft' | 'bindChatDraftSession'
>;

/** All ordinary draft-context transitions over the one bounded registry. */
function useDraftContextActions(stores: ShellStores): DraftContextActions {
  const { setChatDraftRegistry } = stores;
  return {
    setChatDraft: useCallback(
      (draft: string) => setChatDraftRegistry((registry) => writeActiveChatDraft(registry, draft)),
      [setChatDraftRegistry],
    ),
    beginBlankChatDraft: useCallback(() => {
      const contextKey = localChatDraftKey((stores.localChatSequence.current += 1));
      setChatDraftRegistry((registry) => activateChatDraftContext(registry, contextKey, ''));
    }, [stores.localChatSequence, setChatDraftRegistry]),
    openChatSessionDraft: useCallback(
      (sessionId: string) => {
        const contextKey = sessionChatDraftKey(sessionId);
        if (contextKey === null) return;
        setChatDraftRegistry((registry) => activateChatDraftContext(registry, contextKey));
      },
      [setChatDraftRegistry],
    ),
    bindChatDraftSession: useCallback(
      (sessionId: string) =>
        setChatDraftRegistry((registry) => bindActiveChatDraftToSession(registry, sessionId)),
      [setChatDraftRegistry],
    ),
  };
}

function useShellActions(stores: ShellStores): ShellActions {
  const { setChat, setHistoryOpen, setTaskModalId, setMemoryFocus, setToast } = stores;
  const openChat = useCallback(() => setChat('compact'), [setChat]);
  const closeChat = useCallback(() => {
    setChat('closed');
    setHistoryOpen(false);
  }, [setChat, setHistoryOpen]);
  const setChatFull = useCallback((full: boolean) => setChat(full ? 'full' : 'compact'), [setChat]);
  const toggleHistory = useCallback(() => setHistoryOpen((open) => !open), [setHistoryOpen]);
  const closeHistory = useCallback(() => setHistoryOpen(false), [setHistoryOpen]);

  const stepDown = useStepDown(stores);
  const freshChat = useFreshChatActions(stores);
  const draftContexts = useDraftContextActions(stores);

  return {
    openChat,
    ...freshChat,
    ...draftContexts,
    closeChat,
    setChatFull,
    toggleHistory,
    closeHistory,
    stepDown,
    openTaskModal: useCallback((id: string) => setTaskModalId(id), [setTaskModalId]),
    closeTaskModal: useCallback(() => setTaskModalId(null), [setTaskModalId]),
    openMemoryNode: useCallback(
      (nodeId: string, areaPath = '') => setMemoryFocus({ nodeId, areaPath }),
      [setMemoryFocus],
    ),
    clearMemoryFocus: useCallback(() => setMemoryFocus(null), [setMemoryFocus]),
    showToast: useCallback((message: string) => setToast(message), [setToast]),
    clearToast: useCallback(() => setToast(null), [setToast]),
  };
}

/** Construct the shell state machine (used by the App root; exposed via ShellContext to descendants). */
export function useShellState(): ShellState {
  const [chat, setChat] = useState<ChatState>('closed');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [taskModalId, setTaskModalId] = useState<string | null>(null);
  const [memoryFocus, setMemoryFocus] = useState<MemoryFocus | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const localChatSequence = useRef(0);
  const freshChatSequence = useRef(0);
  const [chatDraftRegistry, setChatDraftRegistry] = useState<ChatDraftRegistry>(() =>
    createChatDraftRegistry(localChatDraftKey(0)),
  );
  const [pendingFreshChat, setPendingFreshChat] = useState<FreshChatRequest | null>(null);
  const stores: ShellStores = {
    chat,
    setChat,
    historyOpen,
    setHistoryOpen,
    taskModalId,
    setTaskModalId,
    memoryFocus,
    setMemoryFocus,
    toast,
    setToast,
    chatDraftRegistry,
    setChatDraftRegistry,
    pendingFreshChat,
    setPendingFreshChat,
    localChatSequence,
    freshChatSequence,
  };
  return {
    chat,
    historyOpen,
    taskModalId,
    memoryFocus,
    toast,
    chatDraft: activeChatDraft(chatDraftRegistry),
    pendingFreshChat,
    ...useShellActions(stores),
  };
}
